import React, { useState, useEffect } from 'react';
import type { SaveListVisibility } from '../../services/saves-api';
import { api } from '../../services/api-base';

interface ListVisibilitySelectorProps {
  visibility: SaveListVisibility;
  audienceUserIds: string[];
  onVisibilityChange: (visibility: SaveListVisibility) => void;
  onAudienceChange: (userIds: string[]) => void;
  showDescription?: boolean;
}

interface SearchUser {
  id: string;
  username?: string | null;
  email: string;
  reputation: number;
}

const ListVisibilitySelector: React.FC<ListVisibilitySelectorProps> = ({
  visibility,
  audienceUserIds,
  onVisibilityChange,
  onAudienceChange,
  showDescription = true
}) => {
  const [audienceSearchTerm, setAudienceSearchTerm] = useState('');
  const [audienceSearchResults, setAudienceSearchResults] = useState<SearchUser[]>([]);
  const [selectedAudience, setSelectedAudience] = useState<SearchUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Load selected audience when audienceUserIds change
  useEffect(() => {
    if (audienceUserIds && audienceUserIds.length > 0) {
      // Fetch user details for the IDs
      const fetchAudienceDetails = async () => {
        try {
          const users = await Promise.all(
            audienceUserIds.map(async (userId) => {
              try {
                const response = await api.get(`/users/${userId}/profile`);
                return {
                  id: response.data.id,
                  username: response.data.username,
                  email: response.data.email,
                  reputation: response.data.reputation
                } as SearchUser;
              } catch {
                return null;
              }
            })
          );
          setSelectedAudience(users.filter((u: SearchUser | null): u is SearchUser => u !== null));
        } catch (error) {
          console.error('Error fetching audience details:', error);
        }
      };
      fetchAudienceDetails();
    } else {
      setSelectedAudience([]);
    }
  }, [audienceUserIds]);

  // Search for users when term changes
  useEffect(() => {
    if (!audienceSearchTerm.trim() || visibility !== 'CUSTOM') {
      setAudienceSearchResults([]);
      return;
    }

    const searchUsers = async () => {
      try {
        setIsSearching(true);
        const response = await api.get('/search', {
          params: {
            q: audienceSearchTerm,
            group: 'users',
            limit: 10
          }
        });

        const users = response.data.users?.items || [];
        setAudienceSearchResults(users);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [audienceSearchTerm, visibility]);

  const handleAddAudienceMember = (user: SearchUser) => {
    if (!selectedAudience.find(u => u.id === user.id)) {
      const newAudience = [...selectedAudience, user];
      setSelectedAudience(newAudience);
      onAudienceChange(newAudience.map(u => u.id));
      setAudienceSearchTerm('');
      setAudienceSearchResults([]);
    }
  };

  const handleRemoveAudienceMember = (userId: string) => {
    const newAudience = selectedAudience.filter(u => u.id !== userId);
    setSelectedAudience(newAudience);
    onAudienceChange(newAudience.map(u => u.id));
  };

  const handleVisibilityChange = (newVisibility: SaveListVisibility) => {
    onVisibilityChange(newVisibility);
    if (newVisibility !== 'CUSTOM') {
      setAudienceSearchTerm('');
      setAudienceSearchResults([]);
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
        List Privacy
      </label>

      <select
        value={visibility}
        onChange={(e) => handleVisibilityChange(e.target.value as SaveListVisibility)}
        style={{
          width: '100%',
          padding: '8px',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '14px',
          cursor: 'pointer'
        }}
      >
        <option value="PUBLIC">Public – visible on your profile</option>
        <option value="PRIVATE_SELF">Only me</option>
        <option value="FOLLOWERS">All my followers</option>
        <option value="MUTUALS">Mutual followers</option>
        <option value="CUSTOM">Specific people…</option>
      </select>

      {showDescription && (
        <small style={{ marginTop: '4px', display: 'block', color: '#6b7280' }}>
          Control who can view this list on your profile.
        </small>
      )}

      {visibility === 'CUSTOM' && (
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          marginTop: '12px'
        }}>
          <label style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }}>
            Share with specific users
          </label>

          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by username or email..."
              value={audienceSearchTerm}
              onChange={(e) => setAudienceSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />

            {isSearching && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                padding: '8px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                zIndex: 10
              }}>
                Searching...
              </div>
            )}

            {audienceSearchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                zIndex: 10
              }}>
                {audienceSearchResults.map(user => (
                  <div
                    key={user.id}
                    onClick={() => handleAddAudienceMember(user)}
                    style={{
                      padding: '8px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ fontWeight: '500' }}>
                      {user.username || user.email.split('@')[0]}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {user.email}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedAudience.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Shared with ({selectedAudience.length}):
              </div>
              {selectedAudience.map(user => (
                <div
                  key={user.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px',
                    background: '#f3f4f6',
                    borderRadius: '4px',
                    marginBottom: '4px'
                  }}
                >
                  <span style={{ fontSize: '14px' }}>
                    {user.username || user.email}
                  </span>
                  <button
                    onClick={() => handleRemoveAudienceMember(user.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc2626',
                      cursor: 'pointer',
                      padding: '4px',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ListVisibilitySelector;
